import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebaseConfig'; // Importamos storage
import { collection, query, where, onSnapshot, orderBy, doc, deleteDoc, addDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { syncLedgerHistory } from '../utils/accountingUtils';
// eslint-disable-next-line no-unused-vars
import { IoArrowBack, IoTrash, IoDownloadOutline, IoCloudUploadOutline, IoWalletOutline, IoSyncOutline, IoChevronBack, IoChevronForward, IoDocumentText, IoLockClosed, IoFileTrayFullOutline } from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es'; // Ensure Spanish locale

import './GestionFinanzasScreen.css'; // Import Custom Styles
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

import BalanceGeneral from '../components/FinancialReports/BalanceGeneral';
import EstadoResultados from '../components/FinancialReports/EstadoResultados';
import BalanzaComprobacion from '../components/FinancialReports/BalanzaComprobacion';
import FlujoEfectivo from '../components/FinancialReports/FlujoEfectivo';
import ReporteCxC from '../components/FinancialReports/ReporteCxC';
import ReporteCxP from '../components/FinancialReports/ReporteCxP';
import ReporteProveedores from '../components/FinancialReports/ReporteProveedores';
import RegistrarGastoModal from '../components/RegistrarGastoModal';
import PlanCuentasModal from '../components/PlanCuentasModal';
import CierreFiscalModal from '../components/FinancialReports/CierreFiscalModal';

moment.locale('es');

function GestionFinanzasScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab State for Reports
  const [activeTab, setActiveTab] = useState('resultados'); // 'balance' | 'resultados' | 'balanza' | 'flujo' | 'cxc' | 'cxp'
  const [showRegistrarGasto, setShowRegistrarGasto] = useState(false);
  const [showPlanCuentas, setShowPlanCuentas] = useState(false);
  const [showCierreFiscal, setShowCierreFiscal] = useState(false);

  // Custom Date Filters for Reports
  const [reportMonth, setReportMonth] = useState(moment().month());
  const [reportYear, setReportYear] = useState(moment().year());

  // Sync State
  const [isSyncing, setIsSyncing] = useState(false);

  const tabsScrollRef = useRef(null);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    
    // 1. Wheel horizontal scroll listener
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    
    el.addEventListener('wheel', onWheel, { passive: false });

    // 2. Click & Drag horizontal scroll listener
    let isDown = false;
    let startX;
    let scrollLeftVal;

    const onMouseDown = (e) => {
      isDown = true;
      el.classList.add('active-dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeftVal = el.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown = false;
      el.classList.remove('active-dragging');
    };

    const onMouseUp = () => {
      isDown = false;
      el.classList.remove('active-dragging');
    };

    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5; // scroll-speed multiplier
      el.scrollLeft = scrollLeftVal - walk;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  const handleSync = async () => {
    if (!window.confirm("¿Deseas reconstruir la contabilidad basada en los cobros y pagos existentes? Esto puede tardar unos segundos.")) return;
    setIsSyncing(true);
    try {
      await syncLedgerHistory(userData.condominioId);
      alert("Sincronización completada. Los reportes se actualizarán.");
      // Force reload (simple way) or rely on listeners
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert("Error al sincronizar: " + e.message);
    }
    setIsSyncing(false);
  };

  // Estados para el nuevo formulario de subida
  const [titulo, setTitulo] = useState('');
  const [mes, setMes] = useState(moment().month() + 1); // Mes actual
  const [anio, setAnio] = useState(moment().year()); // Año actual
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');

  // Cargar la lista de reportes existentes
  useEffect(() => {
    if (!userData?.condominioId) return;
    setLoading(true);
    const q = query(
      collection(db, "reportesFinancieros"),
      where("condominioId", "==", userData.condominioId),
      orderBy("anio", "desc"),
      orderBy("mes", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaReportes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReportes(listaReportes);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userData]);

  // Manejar la subida del nuevo reporte
  const handleSubmitReporte = async (e) => {
    e.preventDefault();
    if (!titulo || !archivo) {
      setError("El título y el archivo son obligatorios.");
      return;
    }

    setSubiendo(true);
    setError('');

    try {
      // 1. Subir el archivo (PDF, Excel, etc.) a Storage
      const storageRef = ref(storage, `finanzas/${userData.condominioId}/${anio}-${mes}_${archivo.name}`);
      const snapshot = await uploadBytes(storageRef, archivo);
      const archivoUrl = await getDownloadURL(snapshot.ref);

      // 2. Guardar la referencia en Firestore
      await addDoc(collection(db, "reportesFinancieros"), {
        condominioId: userData.condominioId,
        titulo: titulo,
        mes: Number(mes),
        anio: Number(anio),
        archivoUrl: archivoUrl,
        storagePath: snapshot.ref.fullPath, // Guardamos la ruta para poder borrarlo
        fechaSubida: Timestamp.now()
      });

      // Limpiar formulario
      setTitulo('');
      setArchivo(null);
      e.target.reset();

    } catch (e) {
      console.error("Error al subir reporte: ", e);
      setError("Error al subir el archivo.");
    }
    setSubiendo(false);
  };

  // Manejar el borrado de un reporte
  const handleEliminar = async (reporte) => {
    if (window.confirm(`¿Seguro que quieres eliminar el reporte "${reporte.titulo}"?`)) {
      try {
        // 1. Borrar el archivo de Storage de forma segura si existe ruta
        if (reporte.storagePath) {
          try {
            const fileRef = ref(storage, reporte.storagePath);
            await deleteObject(fileRef);
          } catch (storageError) {
            console.warn("El archivo no se encontró en Storage o ya fue eliminado:", storageError);
          }
        }

        // 2. Borrar el documento de Firestore
        await deleteDoc(doc(db, "reportesFinancieros", reporte.id));

      } catch (e) {
        console.error("Error al eliminar: ", e);
        alert("Error al eliminar el reporte.");
      }
    }
  };

  const handleGenerateMassive = async () => {
    if (!window.confirm("¿Generar datos de 287 residentes en Firestore (Simulación)?\n\nNota: Usaremos UIDs simulados (test_uid_XXX). Esto llenará el directorio y reportes, pero estos usuarios NO podrán iniciar sesión real a menos que sus cuentas de autenticación coincidan manualmente.")) return;

    setLoading(true);
    try {
      // Logic to generate directly in client (Bypassing Cloud Function to avoid connection errors)
      const batch = writeBatch(db);
      let count = 0;
      const total = 287;

      for (let i = 1; i <= total; i++) {
        if (i === 4) continue; // SKIP 004 to preserve your custom changes

        const padded = i.toString().padStart(3, '0');
        // We use a deterministic Fake UID so they are consistent
        const uid = `test_uid_${padded}`;
        const email = `residente${padded}@email.com`;

        const docRef = doc(db, "usuarios", uid);

        const data = {
          uid: uid,
          condominioId: userData.condominioId,
          nombre: `test residente ${padded}`,
          email: email,
          rol: 'residente',
          torre: 'A', // Default filler
          unidad: `Unidad ${i}`, // Match units 1-287
          telefono: `333333${i.toString().padStart(4, '0')}`,
          estatus: 'activo',
          fechaRegistro: Timestamp.now(),
          photoUrl: null,
          requiresVerification: true // <-- FORZAR CÓDIGO DE ACTIVACIÓN
        };

        batch.set(docRef, data, { merge: true });
        count++;
      }

      await batch.commit();
      alert(`¡Éxito! Se han generado/restaurado ${count} residentes en el directorio.\nResidente 004 fue omitido.`);

    } catch (e) {
      console.error(e);
      alert("Error local: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,ID,Nombre,Email,Password,Telefono\n";

    // Admin
    csvContent += "ADMIN,Admin de Prueba 2,admin2@email.com,Password123!,\n";

    // Residents 1 to 287
    for (let i = 1; i <= 287; i++) {
      const padded = i.toString().padStart(3, '0');
      const name = `test residente ${padded}`;
      const email = `residente${padded}@email.com`;
      const pass = `Residente${padded}test`;
      const phone = `333333${i.toString().padStart(4, '0')}`;
      csvContent += `${i},${name},${email},${pass},${phone}\n`;
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "credenciales_condo_test_2.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-finanzas" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Finanzas</h1>
                <p>Transparencia y Presupuesto</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
                  <button onClick={() => setShowPlanCuentas(true)} className="muro-hero-action-btn" title="Catálogo de Cuentas">
                    <IoDocumentText size={20} />
                    <span>Cuentas</span>
                  </button>
                  <button onClick={handleSync} className="muro-hero-action-btn" title="Recalcular Contabilidad" disabled={isSyncing}>
                    <IoSyncOutline size={20} className={isSyncing ? 'spin' : ''} />
                    <span>Sincronizar</span>
                  </button>
                   <button onClick={handleDownloadCSV} className="muro-hero-action-btn" title="Descargar CSV">
                    <IoDownloadOutline size={20} />
                    <span>Descargar</span>
                  </button>
                    <button onClick={() => navigate('/finanzas/carga-saldos')} className="muro-hero-action-btn" title="Cargar Saldos Iniciales Contables">
                      <IoCloudUploadOutline size={20} />
                      <span>Carga Saldos</span>
                    </button>
                    <button onClick={() => setShowCierreFiscal(true)} className="muro-hero-action-btn" title="Cierre de Ejercicio Fiscal">
                      <IoLockClosed size={20} />
                      <span>Cierre Fiscal</span>
                    </button>
                    <button onClick={() => navigate('/obligaciones-fiscales')} className="muro-hero-action-btn" title="Declaraciones Mensuales y SAT">
                      <IoFileTrayFullOutline size={20} />
                      <span>Declaraciones SAT</span>
                    </button>
                </div>
              </div>
              <IoDocumentText className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="finance-dashboard-container">
            {/* --- SECTION 1: FINANCIAL REPORTS DASHBOARD --- */}

      {/* VIEW CONTROLS (Modern Toolbar) */}
      <div className="finance-nav-bar">
        {/* Left: TABS (Scrollable on mobile) */}
        <div ref={tabsScrollRef} className="finance-tabs-scroll-container">
          <div className="finance-tabs">
            <button
              className={`finance-tab-btn ${activeTab === 'resultados' ? 'active' : ''}`}
              onClick={() => setActiveTab('resultados')}
            >
              Resultados
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'balance' ? 'active' : ''}`}
              onClick={() => setActiveTab('balance')}
            >
              Balance
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'balanza' ? 'active' : ''}`}
              onClick={() => setActiveTab('balanza')}
            >
              Balanza
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'flujo' ? 'active' : ''}`}
              onClick={() => setActiveTab('flujo')}
            >
              Flujo Efect.
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'cxc' ? 'active' : ''}`}
              onClick={() => setActiveTab('cxc')}
            >
              CxC
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'cxp' ? 'active' : ''}`}
              onClick={() => setActiveTab('cxp')}
            >
              CxP
            </button>
            <button
              className={`finance-tab-btn ${activeTab === 'proveedores' ? 'active' : ''}`}
              onClick={() => setActiveTab('proveedores')}
            >
              Proveedores
            </button>
          </div>
        </div>

        {/* Right: DATE PICKER */}
        <div className="finance-date-controls">
          <button className="glass-icon-btn" onClick={() => {
            const prev = moment().year(reportYear).month(reportMonth).subtract(1, 'month');
            setReportMonth(prev.month());
            setReportYear(prev.year());
          }} title="Mes Anterior">
            <IoChevronBack size={16} />
          </button>

          <select
            value={reportMonth}
            onChange={e => setReportMonth(Number(e.target.value))}
            className="finance-select"
            style={{ minWidth: '100px', textTransform: 'capitalize' }}
          >
            {moment.months().map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>

          <select
            value={reportYear}
            onChange={e => setReportYear(Number(e.target.value))}
            className="finance-select"
          >
            {Array.from({ length: 5 }, (_, i) => 2023 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <button className="glass-icon-btn" onClick={() => {
            const next = moment().year(reportYear).month(reportMonth).add(1, 'month');
            setReportMonth(next.month());
            setReportYear(next.year());
          }} title="Mes Siguiente">
            <IoChevronForward size={16} />
          </button>
        </div>
      </div>

      {/* REPORT CARD */}
      <div className="finance-report-card">
        <div className="finance-report-header">
          <h2 className="finance-report-title">
            {activeTab === 'resultados' && 'Estado de Resultados'}
            {activeTab === 'balance' && 'Balance General'}
            {activeTab === 'balanza' && 'Balanza de Comprobación'}
            {activeTab === 'flujo' && 'Flujo de Efectivo'}
            {activeTab === 'cxc' && 'Antigüedad de Saldos (CxC)'}
            {activeTab === 'cxp' && 'Antigüedad de Cuentas por Pagar (CxP)'}
            {activeTab === 'proveedores' && 'Análisis de Proveedores'}
          </h2>
          <p className="finance-report-subtitle">
            {moment().month(reportMonth).format('MMMM')} {reportYear}
          </p>
        </div>

        {activeTab === 'resultados' && <EstadoResultados month={reportMonth} year={reportYear} />}
        {activeTab === 'balance' && <BalanceGeneral month={reportMonth} year={reportYear} />}
        {activeTab === 'balanza' && <BalanzaComprobacion month={reportMonth} year={reportYear} />}
        {activeTab === 'flujo' && <FlujoEfectivo month={reportMonth} year={reportYear} />}
        {activeTab === 'cxc' && <ReporteCxC month={reportMonth} year={reportYear} />}
        {activeTab === 'cxp' && <ReporteCxP month={reportMonth} year={reportYear} />}
        {activeTab === 'proveedores' && <ReporteProveedores month={reportMonth} year={reportYear} />}
      </div>

      {showRegistrarGasto && (
        <RegistrarGastoModal
          onClose={() => setShowRegistrarGasto(false)}
          onSuccess={() => {
            // Optional: Reload data or navigate
          }}
        />
      )}

      <div style={{ height: '60px' }}></div>

      {/* --- SECTION 2: TRANSPARENCY REPORTS (UPLOAD & LIST) --- */}
      <div className="upload-section-container">
        <h2 className="section-title"><IoCloudUploadOutline /> Gestión de Documentos y Transparencia</h2>

        {/* Formulario de Subida */}
        <form onSubmit={handleSubmitReporte} className="form-group-section" style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #eee', marginBottom: '30px' }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '15px' }}>Subir Nuevo Archivo</h3>
          {error && <p className="form-mensaje error">{error}</p>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label className="form-label">Título</label>
              <input
                type="text"
                className="form-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ej: Reporte Octubre"
              />
            </div>
            <div>
              <label className="form-label">Mes</label>
              <select value={mes} onChange={(e) => setMes(e.target.value)} className="form-input">
                {moment.months().map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Año</label>
              <input type="number" value={anio} onChange={(e) => setAnio(e.target.value)} className="form-input" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Archivo</label>
            <div 
              onClick={() => document.getElementById('finance-file-upload').click()}
              style={{
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: '#f8fafc',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#6366f1';
                e.currentTarget.style.background = '#f1f5f9';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = '#f8fafc';
              }}
            >
              <IoCloudUploadOutline size={32} style={{ color: archivo ? '#22c55e' : '#64748b' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#334155' }}>
                {archivo ? 'Archivo seleccionado' : 'Selecciona o arrastra tu archivo'}
              </span>
              <span style={{ fontSize: '0.8rem', color: archivo ? '#15803d' : '#64748b', fontWeight: archivo ? 'bold' : 'normal' }}>
                {archivo ? `${archivo.name} (${(archivo.size / 1024).toFixed(1)} KB)` : 'Formatos soportados: PDF, Excel, Imagen'}
              </span>
            </div>
            <input
              id="finance-file-upload"
              type="file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setArchivo(e.target.files[0]);
                  if (!titulo) {
                    const cleanName = e.target.files[0].name.substring(0, e.target.files[0].name.lastIndexOf('.')) || e.target.files[0].name;
                    setTitulo(cleanName);
                  }
                }
              }}
              style={{ display: 'none' }}
            />
          </div>

          <button type="submit" className="action-btn-primary" disabled={subiendo} style={{ maxWidth: '220px' }}>
            {subiendo ? 'Subiendo...' : 'Publicar Reporte'}
          </button>
        </form>

        {/* Lista de Reportes Subidos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {!loading && reportes.map(reporte => (
            <div key={reporte.id} className="aviso-card" style={{ background: 'white', border: '1px solid #eee', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', padding: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="aviso-titulo" style={{ fontSize: '1rem' }}>{reporte.titulo}</span>
                  <span className="aviso-meta" style={{ fontSize: '0.8rem', color: '#999' }}>
                    {moment(reporte.fechaSubida.toDate()).format('D MMM YYYY')} (Periodo: {reporte.mes}/{reporte.anio})
                  </span>
                </div>
                <button
                  className="glass-icon-btn danger-glass"
                  onClick={() => handleEliminar(reporte)}
                >
                  <IoTrash size={18} />
                </button>
              </div>

              <a href={reporte.archivoUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '6px', color: '#0d6efd', fontWeight: '600', textDecoration: 'none', border: '1px solid #e9ecef' }}>
                <IoDownloadOutline /> Descargar
              </a>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height: '40px' }}></div>


      {showPlanCuentas && (
        <PlanCuentasModal
          onClose={() => setShowPlanCuentas(false)}
        />
      )}

      {showCierreFiscal && (
        <CierreFiscalModal
          onClose={() => setShowCierreFiscal(false)}
        />
      )}
          </div>
        </div>
        <KondoBottomNav activeRoute="/gestion-finanzas" />
      </main>
    </div>
  );
}

export default GestionFinanzasScreen;